import React, { useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Button,
  FormLabel,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import InfoIcon from "@mui/icons-material/Info";
import dayjs, { Dayjs } from "dayjs";
import { textStyle } from "../../styles/Style";
import "../../../components/styles/popupStyle.css";
import {
  checkData,
  checkDuplicate,
  deleteItem,
  fillAutocompleteValues,
  updateData,
} from "../../actions/actionItems";
import { removeSampleTestArray } from "../../actions/cleanup";
import {
  buildRetailItemNumberOptions,
  getDefaultRetailItemNumber,
} from "../../../constants/DefaultRetailItems";
import DraggableItemList from "../../subcomponents/DNDComponent";
import { buttonValues, DNDFieldsEnum } from "../../../constants/Types";
import { SpaPropertyEnum } from "../../../constants/NavbarList";
import { ClearButton } from "../../subcomponents/ClearButton";
import { CopyButton } from "../../subcomponents/CopyButton";
import {
  golfTooltipHeaders,
  spaTooltipHeaders,
} from "../../../constants/TooltipInformation";
import { tooltipComponent } from "../../subcomponents/tooltipComponent";
import AlertComponent from "../../subcomponents/AlertComponent";
import {
  validateString,
  validationLength,
  validationMessages,
  validationType,
} from "../../actions/validations";
import {
  SPA_SERVICES_DEFAULT_NO,
  SPA_SERVICES_DEFAULT_YES,
  SPA_SERVICES_FIELD_AUTO_GRATUITY,
  SPA_SERVICES_FIELD_AUTO_GRATUITY_PCT,
  SPA_SERVICES_FIELD_AUTO_SERVICE_CHARGE,
  SPA_SERVICES_FIELD_AUTO_SERVICE_CHARGE_PCT,
  SPA_SERVICES_FIELD_AVAILABLE_ON_WEB,
  SPA_SERVICES_FIELD_AVAILABLE_SUN,
  SPA_SERVICES_FIELD_AVAILABLE_MON,
  SPA_SERVICES_FIELD_AVAILABLE_TUE,
  SPA_SERVICES_FIELD_AVAILABLE_WED,
  SPA_SERVICES_FIELD_AVAILABLE_THU,
  SPA_SERVICES_FIELD_AVAILABLE_FRI,
  SPA_SERVICES_FIELD_AVAILABLE_SAT,
  SPA_SERVICES_FIELD_BREAKDOWN_TIME,
  SPA_SERVICES_FIELD_COMMISSIONABLE,
  SPA_SERVICES_FIELD_EFFECTIVE_FROM_DATE,
  SPA_SERVICES_FIELD_EFFECTIVE_TO_DATE,
  SPA_SERVICES_FIELD_INACTIVE,
  SPA_SERVICES_FIELD_LINKED_ITEM,
  SPA_SERVICES_FIELD_SERVICE_COMMENT,
  SPA_SERVICES_FIELD_PRICE_TYPE,
  SPA_SERVICES_FIELD_SERVICE_EQUIPMENT,
  SPA_SERVICES_FIELD_SERVICE_LOCATIONS,
  SPA_SERVICES_FIELD_MAXIMUM_GUESTS,
  SPA_SERVICES_FIELD_MAXIMUM_STAFF,
  SPA_SERVICES_FIELD_MINIMUM_AGE,
  SPA_SERVICES_FIELD_MINIMUM_GUESTS,
  SPA_SERVICES_FIELD_MINIMUM_STAFF,
  SPA_SERVICES_FIELD_OFFSITE,
  SPA_SERVICES_FIELD_SERVICE_CODE,
  SPA_SERVICES_FIELD_SERVICE_DESCRIPTION,
  SPA_SERVICES_FIELD_SERVICE_GROUP,
  SPA_SERVICES_FIELD_SERVICE_GROUP_CODE,
  SPA_SERVICES_FIELD_SERVICE_GROUP_NAME,
  SPA_SERVICES_FIELD_SERVICE_POINTS,
  SPA_SERVICES_FIELD_SERVICE_PRICE,
  SPA_SERVICES_FIELD_SERVICE_TIME,
  SPA_SERVICES_FIELD_SETUP_TIME,
  SPA_SERVICES_SERVICE_GROUP_PLACEHOLDER,
  SPA_SERVICES_PRICE_TYPE_PLACEHOLDER,
  SPA_SERVICES_SERVICE_EQUIPMENT_PLACEHOLDER,
  SPA_SERVICES_SERVICE_LOCATIONS_PLACEHOLDER,
  SPA_SERVICES_LINKED_ITEM_PLACEHOLDER,
  SPA_SERVICES_VALIDATION_MESSAGES,
  SPA_SERVICES_YES_NO_OPTIONS,
  isSampleSpaServiceGroupRow,
  resolveSpaLinkedMasterSelection,
  resolveSpaServiceGroupCode,
  stringContainsSpaSampleTestMarker,
} from "../../../constants/SpaServicesFields";

const defaultEffectiveDate = () => dayjs();

const normalizeLinkedSelectionsForSave = (
  selections: string[],
  masterRows: unknown,
  codeField: string,
  descriptionField: string,
): string[] =>
  selections
    .map((value) =>
      resolveSpaLinkedMasterSelection(
        value,
        masterRows,
        codeField,
        descriptionField,
      ),
    )
    .filter((value): value is string => Boolean(value));

const toMultiSelectValue = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value.filter(Boolean).map(String);
  }
  if (value != null && value !== "") {
    return [String(value)];
  }
  return [];
};

const mergeSelectedOptions = (
  options: string[],
  selected: string[],
): string[] => {
  const merged = [...options];
  selected.forEach((item) => {
    if (
      item &&
      !merged.includes(item) &&
      !stringContainsSpaSampleTestMarker(item)
    ) {
      merged.push(item);
    }
  });
  return merged;
};

const parseLinkedRetailItemFromOption = (
  option: string | null,
): string | null => {
  if (!option) {
    return null;
  }
  const separator = option.indexOf(" - ");
  return (separator >= 0 ? option.slice(0, separator) : option).trim() || null;
};

const linkedRetailItemToOptionValue = (
  itemNumber: string | null,
  options: string[],
): string | null => {
  if (!itemNumber) {
    return null;
  }
  const match = options.find(
    (o) => o === itemNumber || o.startsWith(`${itemNumber} - `),
  );
  return match ?? itemNumber;
};

const SpaServices = ({
  data,
  fieldValues,
  spaServiceGroups,
  spaPriceTypes,
  spaServiceEquipment,
  spaServiceLocations,
  spaRetailItems,
}: {
  data: (value: any) => void;
  fieldValues: any;
  spaServiceGroups?: any[] | null;
  spaPriceTypes?: any[] | null;
  spaServiceEquipment?: any[] | null;
  spaServiceLocations?: any[] | null;
  spaRetailItems?: any[] | null;
}) => {
  const resolvePayloadList = (
    explicit: any[] | null | undefined,
    key: SpaPropertyEnum,
  ): any[] => {
    if (explicit != null) {
      return explicit;
    }
    if (fieldValues && !Array.isArray(fieldValues)) {
      return fieldValues[key] ?? [];
    }
    return [];
  };

  const serviceGroupsSource = resolvePayloadList(
    spaServiceGroups,
    SpaPropertyEnum.serviceGroups,
  );
  const priceTypesSource = resolvePayloadList(
    spaPriceTypes,
    SpaPropertyEnum.priceTypes,
  );
  const serviceEquipmentSource = resolvePayloadList(
    spaServiceEquipment,
    SpaPropertyEnum.serviceEquipment,
  );
  const serviceLocationsSource = resolvePayloadList(
    spaServiceLocations,
    SpaPropertyEnum.serviceLocations,
  );
  const retailItemsSource = resolvePayloadList(
    spaRetailItems,
    SpaPropertyEnum.retailItems,
  );

  const [serviceGroup, setServiceGroup] = useState<string | null>(null);
  const [serviceCode, setServiceCode] = useState<string | null>(null);
  const [serviceDescription, setServiceDescription] = useState<string | null>(
    null,
  );
  const [servicePoints, setServicePoints] = useState<number | "">("");
  const [effectiveFromDate, setEffectiveFromDate] = useState<Dayjs | null>(
    defaultEffectiveDate,
  );
  const [effectiveToDate, setEffectiveToDate] = useState<Dayjs | null>(
    defaultEffectiveDate,
  );
  const [servicePrice, setServicePrice] = useState<number | "">("");
  const [serviceTime, setServiceTime] = useState<number | "">("");
  const [setupTime, setSetupTime] = useState<number | "">("");
  const [breakdownTime, setBreakdownTime] = useState<number | "">("");
  const [minimumAge, setMinimumAge] = useState<number | "">("");
  const [minimumGuests, setMinimumGuests] = useState<number | "">("");
  const [maximumGuests, setMaximumGuests] = useState<number | "">("");
  const [minimumStaff, setMinimumStaff] = useState<number | "">("");
  const [maximumStaff, setMaximumStaff] = useState<number | "">("");
  const [offsite, setOffsite] = useState<string>(SPA_SERVICES_DEFAULT_NO);
  const [autoGratuity, setAutoGratuity] = useState<string>(
    SPA_SERVICES_DEFAULT_NO,
  );
  const [autoGratuityPct, setAutoGratuityPct] = useState<number | "">("");
  const [autoServiceCharge, setAutoServiceCharge] = useState<string>(
    SPA_SERVICES_DEFAULT_NO,
  );
  const [autoServiceChargePct, setAutoServiceChargePct] = useState<number | "">(
    "",
  );
  const [commissionable, setCommissionable] = useState<string>(
    SPA_SERVICES_DEFAULT_NO,
  );
  const [availableOnWeb, setAvailableOnWeb] = useState<string>(
    SPA_SERVICES_DEFAULT_NO,
  );
  const [availableSun, setAvailableSun] = useState<string>(
    SPA_SERVICES_DEFAULT_YES,
  );
  const [availableMon, setAvailableMon] = useState<string>(
    SPA_SERVICES_DEFAULT_YES,
  );
  const [availableTue, setAvailableTue] = useState<string>(
    SPA_SERVICES_DEFAULT_YES,
  );
  const [availableWed, setAvailableWed] = useState<string>(
    SPA_SERVICES_DEFAULT_YES,
  );
  const [availableThu, setAvailableThu] = useState<string>(
    SPA_SERVICES_DEFAULT_YES,
  );
  const [availableFri, setAvailableFri] = useState<string>(
    SPA_SERVICES_DEFAULT_YES,
  );
  const [availableSat, setAvailableSat] = useState<string>(
    SPA_SERVICES_DEFAULT_YES,
  );
  const [inactive, setInactive] = useState<string>(SPA_SERVICES_DEFAULT_NO);
  const [linkedItem, setLinkedItem] = useState<string | null>(null);
  const [serviceComment, setServiceComment] = useState<string | null>(null);
  const [priceTypes, setPriceTypes] = useState<string[]>([]);
  const [serviceEquipment, setServiceEquipment] = useState<string[]>([]);
  const [serviceLocations, setServiceLocations] = useState<string[]>([]);
  const [editClicked, setEditClicked] = useState<boolean>(false);
  const [alertMessage, setAlertMessage] = useState<string>("");
  const [openAlert, setOpenAlert] = useState<boolean>(false);
  const [alertType, setAlertType] = useState<"success" | "error">("success");
  const [items, setItems] = useState<any[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [lastUpdatedRecord, setLastUpdatedRecord] = useState<any>(null);
  const [serviceCodeFieldError, setServiceCodeFieldError] =
    useState<boolean>(false);
  const [serviceCodeFieldErrorMessage, setServiceCodeFieldErrorMessage] =
    useState<string>("");
  const [serviceDescriptionFieldError, setServiceDescriptionFieldError] =
    useState<boolean>(false);
  const [
    serviceDescriptionFieldErrorMessage,
    setServiceDescriptionFieldErrorMessage,
  ] = useState<string>("");
  const [servicePriceFieldError, setServicePriceFieldError] =
    useState<boolean>(false);
  const [serviceTimeFieldError, setServiceTimeFieldError] =
    useState<boolean>(false);
  const [linkedItemFieldError, setLinkedItemFieldError] =
    useState<boolean>(false);

  const serviceGroupOptions = useMemo(() => {
    const list = Array.isArray(serviceGroupsSource) ? serviceGroupsSource : [];
    const names = list
      .filter((g: any) => !isSampleSpaServiceGroupRow(g))
      .map((g: any) => g?.[SPA_SERVICES_FIELD_SERVICE_GROUP_NAME])
      .filter((n: unknown): n is string => Boolean(n && String(n).trim()));
    return Array.from(new Set(names));
  }, [serviceGroupsSource]);

  const priceTypeOptions = useMemo(
    () =>
      removeSampleTestArray(
        fillAutocompleteValues(
          (priceTypesSource ?? []).filter(
            (row: any) => !isSampleSpaServiceGroupRow(row),
          ),
          DNDFieldsEnum.SPAPRICETYPECODE,
          DNDFieldsEnum.SPAPRICETYPEDESCRIPTION,
        ),
      ),
    [priceTypesSource],
  );

  const serviceEquipmentOptions = useMemo(
    () =>
      removeSampleTestArray(
        fillAutocompleteValues(
          (serviceEquipmentSource ?? []).filter(
            (row: any) => !isSampleSpaServiceGroupRow(row),
          ),
          DNDFieldsEnum.EQUIPMENTCODE,
          DNDFieldsEnum.EQUIPMENTNAME,
        ),
      ),
    [serviceEquipmentSource],
  );

  const serviceLocationOptions = useMemo(
    () =>
      removeSampleTestArray(
        fillAutocompleteValues(
          (serviceLocationsSource ?? []).filter(
            (row: any) => !isSampleSpaServiceGroupRow(row),
          ),
          DNDFieldsEnum.LOCATIONCODE,
          DNDFieldsEnum.LOCATIONNAME,
        ),
      ),
    [serviceLocationsSource],
  );

  const linkedRetailItemOptions = useMemo(
    () => buildRetailItemNumberOptions(retailItemsSource),
    [retailItemsSource],
  );

  useEffect(() => {
    if (fieldValues) {
      setItems(
        Array.isArray(fieldValues)
          ? fieldValues
          : (fieldValues[SpaPropertyEnum.services] ?? []),
      );
    }
  }, []);

  useEffect(() => {
    data(items);
    setSelectedId(checkData(items, selectedId) ? selectedId : null);
  }, [items]);

  const validateServiceFields = (): boolean => {
    if (!serviceGroup || String(serviceGroup).trim().length === 0) {
      setAlertMessage(SPA_SERVICES_VALIDATION_MESSAGES.serviceGroupMandatory);
      setAlertType("error");
      setOpenAlert(true);
      return false;
    }

    if (!serviceCode || serviceCode.length === 0) {
      setAlertMessage(SPA_SERVICES_VALIDATION_MESSAGES.serviceCodeMandatory);
      setAlertType("error");
      setOpenAlert(true);
      return false;
    }
    setServiceCodeFieldError(false);
    setServiceCodeFieldErrorMessage("");
    if (
      !validateString(
        serviceCode,
        validationLength.serviceCode,
        validationType.CODE,
      )
    ) {
      setServiceCodeFieldError(true);
      setServiceCodeFieldErrorMessage(validationMessages.serviceCode);
      return false;
    }

    if (!serviceDescription || serviceDescription.length === 0) {
      setAlertMessage(
        SPA_SERVICES_VALIDATION_MESSAGES.serviceDescriptionMandatory,
      );
      setAlertType("error");
      setOpenAlert(true);
      return false;
    }
    setServiceDescriptionFieldError(false);
    setServiceDescriptionFieldErrorMessage("");
    if (
      !validateString(
        serviceDescription,
        validationLength.serviceDescription,
        validationType.NAME,
      )
    ) {
      setServiceDescriptionFieldError(true);
      setServiceDescriptionFieldErrorMessage(
        validationMessages.serviceDescription,
      );
      return false;
    }

    const listForDuplicate = selectedId
      ? items.filter((i: any) => String(i.id) !== String(selectedId))
      : items;
    if (
      checkDuplicate(listForDuplicate, DNDFieldsEnum.SERVICECODE, serviceCode)
    ) {
      setAlertMessage(SPA_SERVICES_VALIDATION_MESSAGES.duplicateEntry);
      setAlertType("error");
      setOpenAlert(true);
      return false;
    }

    if (servicePrice === "" || servicePrice === undefined) {
      setAlertMessage(SPA_SERVICES_VALIDATION_MESSAGES.priceMandatory);
      setAlertType("error");
      setOpenAlert(true);
      setServicePriceFieldError(true);
      return false;
    }
    setServicePriceFieldError(false);

    if (
      serviceTime === "" ||
      serviceTime === undefined ||
      Number(serviceTime) <= 0
    ) {
      setAlertMessage(SPA_SERVICES_VALIDATION_MESSAGES.serviceTimeMandatory);
      setAlertType("error");
      setOpenAlert(true);
      setServiceTimeFieldError(true);
      return false;
    }
    setServiceTimeFieldError(false);

    if (!linkedItem || String(linkedItem).trim().length === 0) {
      setAlertMessage(SPA_SERVICES_VALIDATION_MESSAGES.linkedItemMandatory);
      setAlertType("error");
      setOpenAlert(true);
      setLinkedItemFieldError(true);
      return false;
    }
    setLinkedItemFieldError(false);

    return true;
  };

  const updateItem = () => {
    if (!validateServiceFields()) {
      return false;
    }
    setItems((prev) =>
      prev.map((item: any) =>
        item.id === selectedId ? { ...item, ...payload } : item,
      ),
    );
    editItem(null);
    setEditClicked(false);
    setSelectedId(null);
    setAlertMessage(`${SpaPropertyEnum.services} updated successfully`);
    setAlertType("success");
    setOpenAlert(true);
  };

  const addItem = () => {
    if (!validateServiceFields()) {
      return false;
    }
    setItems((prev) =>
      updateData(
        prev?.length
          ? [...prev, { id: prev.length + 1, ...payload }]
          : [{ id: 1, ...payload }],
      ),
    );
    setLastUpdatedRecord(payload);
    editItem(null);
    setEditClicked(false);
  };

  const editItem = (list: any) => {
    list?.id && setSelectedId(list.id);
    if (list != null) {
      setEditClicked(true);
      setServiceGroup(list[SPA_SERVICES_FIELD_SERVICE_GROUP] ?? null);
      setServiceCode(list[SPA_SERVICES_FIELD_SERVICE_CODE] ?? null);
      setServiceDescription(
        list[SPA_SERVICES_FIELD_SERVICE_DESCRIPTION] ?? null,
      );
      setServicePoints(
        list[SPA_SERVICES_FIELD_SERVICE_POINTS] !== undefined &&
          list[SPA_SERVICES_FIELD_SERVICE_POINTS] !== null
          ? Number(list[SPA_SERVICES_FIELD_SERVICE_POINTS])
          : "",
      );
      setEffectiveFromDate(
        list[SPA_SERVICES_FIELD_EFFECTIVE_FROM_DATE]
          ? dayjs(list[SPA_SERVICES_FIELD_EFFECTIVE_FROM_DATE])
          : defaultEffectiveDate(),
      );
      setEffectiveToDate(
        list[SPA_SERVICES_FIELD_EFFECTIVE_TO_DATE]
          ? dayjs(list[SPA_SERVICES_FIELD_EFFECTIVE_TO_DATE])
          : defaultEffectiveDate(),
      );
      setServicePrice(
        list[SPA_SERVICES_FIELD_SERVICE_PRICE] !== undefined &&
          list[SPA_SERVICES_FIELD_SERVICE_PRICE] !== null
          ? Number(list[SPA_SERVICES_FIELD_SERVICE_PRICE])
          : "",
      );
      setServiceTime(
        list[SPA_SERVICES_FIELD_SERVICE_TIME] !== undefined &&
          list[SPA_SERVICES_FIELD_SERVICE_TIME] !== null
          ? Number(list[SPA_SERVICES_FIELD_SERVICE_TIME])
          : "",
      );
      setSetupTime(
        list[SPA_SERVICES_FIELD_SETUP_TIME] !== undefined &&
          list[SPA_SERVICES_FIELD_SETUP_TIME] !== null
          ? Number(list[SPA_SERVICES_FIELD_SETUP_TIME])
          : "",
      );
      setBreakdownTime(
        list[SPA_SERVICES_FIELD_BREAKDOWN_TIME] !== undefined &&
          list[SPA_SERVICES_FIELD_BREAKDOWN_TIME] !== null
          ? Number(list[SPA_SERVICES_FIELD_BREAKDOWN_TIME])
          : "",
      );
      setMinimumAge(
        list[SPA_SERVICES_FIELD_MINIMUM_AGE] !== undefined &&
          list[SPA_SERVICES_FIELD_MINIMUM_AGE] !== null
          ? Number(list[SPA_SERVICES_FIELD_MINIMUM_AGE])
          : "",
      );
      setMinimumGuests(
        list[SPA_SERVICES_FIELD_MINIMUM_GUESTS] !== undefined &&
          list[SPA_SERVICES_FIELD_MINIMUM_GUESTS] !== null
          ? Number(list[SPA_SERVICES_FIELD_MINIMUM_GUESTS])
          : "",
      );
      setMaximumGuests(
        list[SPA_SERVICES_FIELD_MAXIMUM_GUESTS] !== undefined &&
          list[SPA_SERVICES_FIELD_MAXIMUM_GUESTS] !== null
          ? Number(list[SPA_SERVICES_FIELD_MAXIMUM_GUESTS])
          : "",
      );
      setMinimumStaff(
        list[SPA_SERVICES_FIELD_MINIMUM_STAFF] !== undefined &&
          list[SPA_SERVICES_FIELD_MINIMUM_STAFF] !== null
          ? Number(list[SPA_SERVICES_FIELD_MINIMUM_STAFF])
          : "",
      );
      setMaximumStaff(
        list[SPA_SERVICES_FIELD_MAXIMUM_STAFF] !== undefined &&
          list[SPA_SERVICES_FIELD_MAXIMUM_STAFF] !== null
          ? Number(list[SPA_SERVICES_FIELD_MAXIMUM_STAFF])
          : "",
      );
      setOffsite(list?.[SPA_SERVICES_FIELD_OFFSITE] ?? SPA_SERVICES_DEFAULT_NO);
      setAutoGratuity(
        list[SPA_SERVICES_FIELD_AUTO_GRATUITY] ?? SPA_SERVICES_DEFAULT_NO,
      );
      setAutoGratuityPct(
        list[SPA_SERVICES_FIELD_AUTO_GRATUITY_PCT] !== undefined &&
          list[SPA_SERVICES_FIELD_AUTO_GRATUITY_PCT] !== null
          ? Number(list[SPA_SERVICES_FIELD_AUTO_GRATUITY_PCT])
          : "",
      );
      setAutoServiceCharge(
        list[SPA_SERVICES_FIELD_AUTO_SERVICE_CHARGE] ?? SPA_SERVICES_DEFAULT_NO,
      );
      setAutoServiceChargePct(
        list[SPA_SERVICES_FIELD_AUTO_SERVICE_CHARGE_PCT] !== undefined &&
          list[SPA_SERVICES_FIELD_AUTO_SERVICE_CHARGE_PCT] !== null
          ? Number(list[SPA_SERVICES_FIELD_AUTO_SERVICE_CHARGE_PCT])
          : "",
      );
      setCommissionable(
        list?.[SPA_SERVICES_FIELD_COMMISSIONABLE] ?? SPA_SERVICES_DEFAULT_NO,
      );
      setAvailableOnWeb(
        list[SPA_SERVICES_FIELD_AVAILABLE_ON_WEB] ?? SPA_SERVICES_DEFAULT_NO,
      );
      setAvailableSun(
        list[SPA_SERVICES_FIELD_AVAILABLE_SUN] ?? SPA_SERVICES_DEFAULT_YES,
      );
      setAvailableMon(
        list[SPA_SERVICES_FIELD_AVAILABLE_MON] ?? SPA_SERVICES_DEFAULT_YES,
      );
      setAvailableTue(
        list[SPA_SERVICES_FIELD_AVAILABLE_TUE] ?? SPA_SERVICES_DEFAULT_YES,
      );
      setAvailableWed(
        list[SPA_SERVICES_FIELD_AVAILABLE_WED] ?? SPA_SERVICES_DEFAULT_YES,
      );
      setAvailableThu(
        list[SPA_SERVICES_FIELD_AVAILABLE_THU] ?? SPA_SERVICES_DEFAULT_YES,
      );
      setAvailableFri(
        list[SPA_SERVICES_FIELD_AVAILABLE_FRI] ?? SPA_SERVICES_DEFAULT_YES,
      );
      setAvailableSat(
        list[SPA_SERVICES_FIELD_AVAILABLE_SAT] ?? SPA_SERVICES_DEFAULT_YES,
      );
      setInactive(
        list?.[SPA_SERVICES_FIELD_INACTIVE] ?? SPA_SERVICES_DEFAULT_NO,
      );
      setLinkedItem(
        list[SPA_SERVICES_FIELD_LINKED_ITEM] != null &&
          list[SPA_SERVICES_FIELD_LINKED_ITEM] !== ""
          ? String(list[SPA_SERVICES_FIELD_LINKED_ITEM])
          : String(getDefaultRetailItemNumber()),
      );
      setServiceComment(list[SPA_SERVICES_FIELD_SERVICE_COMMENT] ?? null);
      setPriceTypes(toMultiSelectValue(list[SPA_SERVICES_FIELD_PRICE_TYPE]));
      setServiceEquipment(
        toMultiSelectValue(list[SPA_SERVICES_FIELD_SERVICE_EQUIPMENT]),
      );
      setServiceLocations(
        toMultiSelectValue(list[SPA_SERVICES_FIELD_SERVICE_LOCATIONS]),
      );
    } else {
      setServiceGroup(null);
      setServiceCode(null);
      setServiceDescription(null);
      setServicePoints("");
      setEffectiveFromDate(defaultEffectiveDate());
      setEffectiveToDate(defaultEffectiveDate());
      setServicePrice("");
      setServiceTime("");
      setSetupTime("");
      setBreakdownTime("");
      setMinimumAge("");
      setMinimumGuests("");
      setMaximumGuests("");
      setMinimumStaff("");
      setMaximumStaff("");
      setOffsite(SPA_SERVICES_DEFAULT_NO);
      setAutoGratuity(SPA_SERVICES_DEFAULT_NO);
      setAutoGratuityPct("");
      setAutoServiceCharge(SPA_SERVICES_DEFAULT_NO);
      setAutoServiceChargePct("");
      setCommissionable(SPA_SERVICES_DEFAULT_NO);
      setAvailableOnWeb(SPA_SERVICES_DEFAULT_NO);
      setAvailableSun(SPA_SERVICES_DEFAULT_YES);
      setAvailableMon(SPA_SERVICES_DEFAULT_YES);
      setAvailableTue(SPA_SERVICES_DEFAULT_YES);
      setAvailableWed(SPA_SERVICES_DEFAULT_YES);
      setAvailableThu(SPA_SERVICES_DEFAULT_YES);
      setAvailableFri(SPA_SERVICES_DEFAULT_YES);
      setAvailableSat(SPA_SERVICES_DEFAULT_YES);
      setInactive(SPA_SERVICES_DEFAULT_NO);
      setLinkedItem(String(getDefaultRetailItemNumber()));
      setServiceComment(null);
      setPriceTypes([]);
      setServiceEquipment([]);
      setServiceLocations([]);
      setServiceCodeFieldError(false);
      setServiceCodeFieldErrorMessage("");
      setServiceDescriptionFieldError(false);
      setServiceDescriptionFieldErrorMessage("");
      setServicePriceFieldError(false);
      setServiceTimeFieldError(false);
      setLinkedItemFieldError(false);
    }
  };

  const deleteItemById = (id: number) => {
    setItems(deleteItem(items, id));
  };

  const availableDaysConfig = [
    {
      label: "Sun",
      tooltip: spaTooltipHeaders.serviceAvailableSun,
      value: availableSun,
      setValue: setAvailableSun,
    },
    {
      label: "Mon",
      tooltip: spaTooltipHeaders.serviceAvailableMon,
      value: availableMon,
      setValue: setAvailableMon,
    },
    {
      label: "Tue",
      tooltip: spaTooltipHeaders.serviceAvailableTue,
      value: availableTue,
      setValue: setAvailableTue,
    },
    {
      label: "Wed",
      tooltip: spaTooltipHeaders.serviceAvailableWed,
      value: availableWed,
      setValue: setAvailableWed,
    },
    {
      label: "Thu",
      tooltip: spaTooltipHeaders.serviceAvailableThu,
      value: availableThu,
      setValue: setAvailableThu,
    },
    {
      label: "Fri",
      tooltip: spaTooltipHeaders.serviceAvailableFri,
      value: availableFri,
      setValue: setAvailableFri,
    },
    {
      label: "Sat",
      tooltip: spaTooltipHeaders.serviceAvailableSat,
      value: availableSat,
      setValue: setAvailableSat,
    },
  ];

  const payload = {
    [SPA_SERVICES_FIELD_SERVICE_GROUP]: serviceGroup,
    [SPA_SERVICES_FIELD_SERVICE_GROUP_CODE]: resolveSpaServiceGroupCode(
      serviceGroup,
      serviceGroupsSource,
    ),
    [SPA_SERVICES_FIELD_SERVICE_CODE]: serviceCode,
    [SPA_SERVICES_FIELD_SERVICE_DESCRIPTION]: serviceDescription,
    [SPA_SERVICES_FIELD_SERVICE_POINTS]:
      servicePoints === "" ? undefined : Number(servicePoints),
    [SPA_SERVICES_FIELD_EFFECTIVE_FROM_DATE]: (
      effectiveFromDate ?? defaultEffectiveDate()
    ).format("YYYY-MM-DD"),
    [SPA_SERVICES_FIELD_EFFECTIVE_TO_DATE]: (
      effectiveToDate ?? defaultEffectiveDate()
    ).format("YYYY-MM-DD"),
    [SPA_SERVICES_FIELD_SERVICE_PRICE]:
      servicePrice === "" ? undefined : Number(servicePrice),
    [SPA_SERVICES_FIELD_SERVICE_TIME]:
      serviceTime === "" ? undefined : Number(serviceTime),
    [SPA_SERVICES_FIELD_SETUP_TIME]:
      setupTime === "" ? undefined : Number(setupTime),
    [SPA_SERVICES_FIELD_BREAKDOWN_TIME]:
      breakdownTime === "" ? undefined : Number(breakdownTime),
    [SPA_SERVICES_FIELD_MINIMUM_AGE]:
      minimumAge === "" ? undefined : Number(minimumAge),
    [SPA_SERVICES_FIELD_MINIMUM_GUESTS]:
      minimumGuests === "" ? undefined : Number(minimumGuests),
    [SPA_SERVICES_FIELD_MAXIMUM_GUESTS]:
      maximumGuests === "" ? undefined : Number(maximumGuests),
    [SPA_SERVICES_FIELD_MINIMUM_STAFF]:
      minimumStaff === "" ? undefined : Number(minimumStaff),
    [SPA_SERVICES_FIELD_MAXIMUM_STAFF]:
      maximumStaff === "" ? undefined : Number(maximumStaff),
    [SPA_SERVICES_FIELD_OFFSITE]: offsite,
    [SPA_SERVICES_FIELD_AUTO_GRATUITY]: autoGratuity,
    [SPA_SERVICES_FIELD_AUTO_GRATUITY_PCT]:
      autoGratuityPct === "" ? undefined : Number(autoGratuityPct),
    [SPA_SERVICES_FIELD_AUTO_SERVICE_CHARGE]: autoServiceCharge,
    [SPA_SERVICES_FIELD_AUTO_SERVICE_CHARGE_PCT]:
      autoServiceChargePct === "" ? undefined : Number(autoServiceChargePct),
    [SPA_SERVICES_FIELD_COMMISSIONABLE]: commissionable,
    [SPA_SERVICES_FIELD_AVAILABLE_ON_WEB]: availableOnWeb,
    [SPA_SERVICES_FIELD_AVAILABLE_SUN]: availableSun,
    [SPA_SERVICES_FIELD_AVAILABLE_MON]: availableMon,
    [SPA_SERVICES_FIELD_AVAILABLE_TUE]: availableTue,
    [SPA_SERVICES_FIELD_AVAILABLE_WED]: availableWed,
    [SPA_SERVICES_FIELD_AVAILABLE_THU]: availableThu,
    [SPA_SERVICES_FIELD_AVAILABLE_FRI]: availableFri,
    [SPA_SERVICES_FIELD_AVAILABLE_SAT]: availableSat,
    [SPA_SERVICES_FIELD_INACTIVE]: inactive,
    [SPA_SERVICES_FIELD_LINKED_ITEM]: linkedItem,
    [SPA_SERVICES_FIELD_SERVICE_COMMENT]: serviceComment,
    [SPA_SERVICES_FIELD_PRICE_TYPE]: normalizeLinkedSelectionsForSave(
      priceTypes,
      priceTypesSource,
      "Price Type Code",
      "Price Type Description",
    ),
    [SPA_SERVICES_FIELD_SERVICE_EQUIPMENT]: normalizeLinkedSelectionsForSave(
      serviceEquipment,
      serviceEquipmentSource,
      "Equipment Code",
      "Equipment Description",
    ),
    [SPA_SERVICES_FIELD_SERVICE_LOCATIONS]: serviceLocations,
  };

  return (
    <Stack direction="column" gap="10px">
      <Paper
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "10px",
          padding: "20px",
        }}
      >
        <Stack
          direction="row"
          width="100%"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Typography variant="h6" sx={textStyle}>
            Services
            <Tooltip
              title={tooltipComponent(
                spaTooltipHeaders?.serviceHeader ??
                  golfTooltipHeaders?.categoryGroup ??
                  "Services",
              )}
            >
              <InfoIcon fontSize="small" />
            </Tooltip>
          </Typography>
          <Stack direction="row" gap="10px">
            {editClicked && (
              <ClearButton
                click={() => {
                  editItem(null);
                  setSelectedId(null);
                }}
              />
            )}
            {lastUpdatedRecord && (
              <CopyButton click={() => editItem(lastUpdatedRecord)} />
            )}
          </Stack>
        </Stack>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Stack
            className="form-two-col-row"
            direction="row"
            flexWrap="wrap"
            gap="12px"
          >
            <Stack direction="column" className="form-two-col-field">
              <Stack direction="row">
                <FormLabel>Service Group *</FormLabel>
                <Tooltip
                  title={tooltipComponent(
                    spaTooltipHeaders.serviceServiceGroup,
                  )}
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </Stack>
              <Autocomplete
                options={
                  serviceGroup &&
                  !serviceGroupOptions.includes(serviceGroup) &&
                  !stringContainsSpaSampleTestMarker(serviceGroup)
                    ? [...serviceGroupOptions, serviceGroup]
                    : serviceGroupOptions
                }
                value={serviceGroup}
                onChange={(_, v) => setServiceGroup(v ?? null)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    size="small"
                    placeholder={SPA_SERVICES_SERVICE_GROUP_PLACEHOLDER}
                  />
                )}
              />
            </Stack>
            <Stack direction="column" className="form-two-col-field">
              <Stack direction="row">
                <FormLabel>Service Code *</FormLabel>
                <Tooltip
                  title={tooltipComponent(spaTooltipHeaders.serviceServiceCode)}
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </Stack>
              <TextField
                variant="outlined"
                size="small"
                value={serviceCode ?? ""}
                helperText={
                  serviceCodeFieldError ? serviceCodeFieldErrorMessage : ""
                }
                error={serviceCodeFieldError}
                onChange={(e) => setServiceCode(e.target.value || null)}
                fullWidth
              />
            </Stack>
            <Stack direction="column" className="form-two-col-field">
              <Stack direction="row">
                <FormLabel>Service Description *</FormLabel>
                <Tooltip
                  title={tooltipComponent(
                    spaTooltipHeaders.serviceServiceDescription,
                  )}
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </Stack>
              <TextField
                variant="outlined"
                size="small"
                value={serviceDescription ?? ""}
                onChange={(e) => setServiceDescription(e.target.value || null)}
                error={serviceDescriptionFieldError}
                helperText={
                  serviceDescriptionFieldError
                    ? serviceDescriptionFieldErrorMessage
                    : ""
                }
                fullWidth
              />
            </Stack>
            <Stack direction="column" className="form-two-col-field">
              <Stack direction="row">
                <FormLabel>Price Type</FormLabel>
                <Tooltip
                  title={tooltipComponent(spaTooltipHeaders.servicePriceType)}
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </Stack>
              <Autocomplete
                multiple
                disablePortal
                size="small"
                options={mergeSelectedOptions(priceTypeOptions, priceTypes)}
                value={priceTypes}
                onChange={(_, v) => setPriceTypes(v)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    placeholder={SPA_SERVICES_PRICE_TYPE_PLACEHOLDER}
                  />
                )}
              />
            </Stack>
            <Stack direction="column" className="form-two-col-field">
              <Stack direction="row">
                <FormLabel>Service Equipment</FormLabel>
                <Tooltip
                  title={tooltipComponent(
                    spaTooltipHeaders.serviceServiceEquipment,
                  )}
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </Stack>
              <Autocomplete
                multiple
                disablePortal
                size="small"
                options={mergeSelectedOptions(
                  serviceEquipmentOptions,
                  serviceEquipment,
                )}
                value={serviceEquipment}
                onChange={(_, v) => setServiceEquipment(v)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    placeholder={SPA_SERVICES_SERVICE_EQUIPMENT_PLACEHOLDER}
                  />
                )}
              />
            </Stack>
            <Stack direction="column" className="form-two-col-field">
              <Stack direction="row">
                <FormLabel>Service Locations</FormLabel>
                <Tooltip
                  title={tooltipComponent(
                    spaTooltipHeaders.serviceServiceLocations,
                  )}
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </Stack>
              <Autocomplete
                multiple
                disablePortal
                size="small"
                options={mergeSelectedOptions(
                  serviceLocationOptions,
                  serviceLocations,
                )}
                value={serviceLocations}
                onChange={(_, v) => setServiceLocations(v)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    placeholder={SPA_SERVICES_SERVICE_LOCATIONS_PLACEHOLDER}
                  />
                )}
              />
            </Stack>
            <Stack direction="column" className="form-two-col-field">
              <Stack direction="row">
                <FormLabel>Points</FormLabel>
                <Tooltip
                  title={tooltipComponent(
                    spaTooltipHeaders.serviceServicePoints,
                  )}
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </Stack>
              <TextField
                type="number"
                variant="outlined"
                size="small"
                value={servicePoints}
                onChange={(e) =>
                  setServicePoints(
                    e.target.value === ""
                      ? ""
                      : parseInt(e.target.value, 10) || "",
                  )
                }
                fullWidth
              />
            </Stack>
            <Stack direction="column" className="form-two-col-field">
              <Stack direction="row">
                <FormLabel>Price *</FormLabel>
                <Tooltip
                  title={tooltipComponent(
                    spaTooltipHeaders.serviceServicePrice,
                  )}
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </Stack>
              <TextField
                type="number"
                inputProps={{ step: 0.01 }}
                variant="outlined"
                size="small"
                value={servicePrice}
                error={servicePriceFieldError}
                helperText={
                  servicePriceFieldError ? validationMessages.fieldRequired : ""
                }
                onChange={(e) => {
                  setServicePrice(
                    e.target.value === ""
                      ? ""
                      : parseFloat(e.target.value) || "",
                  );
                  setServicePriceFieldError(false);
                }}
                fullWidth
              />
            </Stack>
            <Stack direction="column" className="form-two-col-field">
              <Stack direction="row">
                <FormLabel>Service Time (min) *</FormLabel>
                <Tooltip
                  title={tooltipComponent(spaTooltipHeaders.serviceServiceTime)}
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </Stack>
              <TextField
                type="number"
                variant="outlined"
                size="small"
                value={serviceTime}
                error={serviceTimeFieldError}
                helperText={
                  serviceTimeFieldError ? validationMessages.fieldRequired : ""
                }
                onChange={(e) => {
                  setServiceTime(
                    e.target.value === ""
                      ? ""
                      : parseInt(e.target.value, 10) || "",
                  );
                  setServiceTimeFieldError(false);
                }}
                fullWidth
              />
            </Stack>
            <Stack direction="column" className="form-two-col-field">
              <Stack direction="row">
                <FormLabel>Setup Time</FormLabel>
                <Tooltip
                  title={tooltipComponent(spaTooltipHeaders.serviceSetupTime)}
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </Stack>
              <TextField
                type="number"
                variant="outlined"
                size="small"
                value={setupTime}
                onChange={(e) =>
                  setSetupTime(
                    e.target.value === ""
                      ? ""
                      : parseInt(e.target.value, 10) || "",
                  )
                }
                fullWidth
              />
            </Stack>
            <Stack direction="column" className="form-two-col-field">
              <Stack direction="row">
                <FormLabel>Breakdown Time</FormLabel>
                <Tooltip
                  title={tooltipComponent(
                    spaTooltipHeaders.serviceBreakdownTime,
                  )}
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </Stack>
              <TextField
                type="number"
                variant="outlined"
                size="small"
                value={breakdownTime}
                onChange={(e) =>
                  setBreakdownTime(
                    e.target.value === ""
                      ? ""
                      : parseInt(e.target.value, 10) || "",
                  )
                }
                fullWidth
              />
            </Stack>
            <Stack direction="column" className="form-two-col-field">
              <Stack direction="row">
                <FormLabel>Min Age</FormLabel>
                <Tooltip
                  title={tooltipComponent(spaTooltipHeaders.serviceMinimumAge)}
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </Stack>
              <TextField
                type="number"
                variant="outlined"
                size="small"
                value={minimumAge}
                onChange={(e) =>
                  setMinimumAge(
                    e.target.value === ""
                      ? ""
                      : parseInt(e.target.value, 10) || "",
                  )
                }
                fullWidth
              />
            </Stack>
            <Stack direction="column" className="form-two-col-field">
              <Stack direction="row">
                <FormLabel>Min Guests</FormLabel>
                <Tooltip
                  title={tooltipComponent(
                    spaTooltipHeaders.serviceMinimumGuests,
                  )}
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </Stack>
              <TextField
                type="number"
                variant="outlined"
                size="small"
                value={minimumGuests}
                onChange={(e) =>
                  setMinimumGuests(
                    e.target.value === ""
                      ? ""
                      : parseInt(e.target.value, 10) || "",
                  )
                }
                fullWidth
              />
            </Stack>
            <Stack direction="column" className="form-two-col-field">
              <Stack direction="row">
                <FormLabel>Max Guests</FormLabel>
                <Tooltip
                  title={tooltipComponent(
                    spaTooltipHeaders.serviceMaximumGuests,
                  )}
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </Stack>
              <TextField
                type="number"
                variant="outlined"
                size="small"
                value={maximumGuests}
                onChange={(e) =>
                  setMaximumGuests(
                    e.target.value === ""
                      ? ""
                      : parseInt(e.target.value, 10) || "",
                  )
                }
                fullWidth
              />
            </Stack>
            <Stack direction="column" className="form-two-col-field">
              <Stack direction="row">
                <FormLabel>Min Staff</FormLabel>
                <Tooltip
                  title={tooltipComponent(
                    spaTooltipHeaders.serviceMinimumStaff,
                  )}
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </Stack>
              <TextField
                type="number"
                variant="outlined"
                size="small"
                value={minimumStaff}
                onChange={(e) =>
                  setMinimumStaff(
                    e.target.value === ""
                      ? ""
                      : parseInt(e.target.value, 10) || "",
                  )
                }
                fullWidth
              />
            </Stack>
            <Stack direction="column" className="form-two-col-field">
              <Stack direction="row">
                <FormLabel>Max Staff</FormLabel>
                <Tooltip
                  title={tooltipComponent(
                    spaTooltipHeaders.serviceMaximumStaff,
                  )}
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </Stack>
              <TextField
                type="number"
                variant="outlined"
                size="small"
                value={maximumStaff}
                onChange={(e) =>
                  setMaximumStaff(
                    e.target.value === ""
                      ? ""
                      : parseInt(e.target.value, 10) || "",
                  )
                }
                fullWidth
              />
            </Stack>
            <Stack direction="column" className="form-two-col-field">
              <Stack direction="row">
                <FormLabel>Effective From</FormLabel>
                <Tooltip
                  title={tooltipComponent(
                    spaTooltipHeaders.serviceEffectiveFromDate,
                  )}
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </Stack>
              <DatePicker
                value={effectiveFromDate}
                onChange={(v) => setEffectiveFromDate(v)}
                slotProps={{ textField: { size: "small", fullWidth: true } }}
              />
            </Stack>
            <Stack direction="column" className="form-two-col-field">
              <Stack direction="row">
                <FormLabel>Effective To</FormLabel>
                <Tooltip
                  title={tooltipComponent(
                    spaTooltipHeaders.serviceEffectiveToDate,
                  )}
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </Stack>
              <DatePicker
                value={effectiveToDate}
                onChange={(v) => setEffectiveToDate(v)}
                slotProps={{ textField: { size: "small", fullWidth: true } }}
              />
            </Stack>
            <Stack direction="column" className="form-two-col-field">
              <Stack direction="row">
                <FormLabel>Offsite</FormLabel>
                <Tooltip
                  title={tooltipComponent(spaTooltipHeaders.serviceOffsite)}
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </Stack>
              <Autocomplete
                size="small"
                options={SPA_SERVICES_YES_NO_OPTIONS}
                value={offsite}
                onChange={(_, v) => setOffsite(v ?? SPA_SERVICES_DEFAULT_NO)}
                renderInput={(params) => (
                  <TextField {...params} variant="outlined" />
                )}
              />
            </Stack>
            <Stack direction="column" className="form-two-col-field">
              <Stack direction="row">
                <FormLabel>Auto Gratuity</FormLabel>
                <Tooltip
                  title={tooltipComponent(
                    spaTooltipHeaders.serviceAutoGratuity,
                  )}
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </Stack>
              <Autocomplete
                size="small"
                options={SPA_SERVICES_YES_NO_OPTIONS}
                value={autoGratuity}
                onChange={(_, v) =>
                  setAutoGratuity(v ?? SPA_SERVICES_DEFAULT_NO)
                }
                renderInput={(params) => (
                  <TextField {...params} variant="outlined" />
                )}
              />
            </Stack>
            <Stack direction="column" className="form-two-col-field">
              <Stack direction="row">
                <FormLabel>Auto Grat %</FormLabel>
                <Tooltip
                  title={tooltipComponent(
                    spaTooltipHeaders.serviceAutoGratuityPercentage,
                  )}
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </Stack>
              <TextField
                type="number"
                inputProps={{ step: 0.01 }}
                variant="outlined"
                size="small"
                value={autoGratuityPct}
                onChange={(e) =>
                  setAutoGratuityPct(
                    e.target.value === ""
                      ? ""
                      : parseFloat(e.target.value) || "",
                  )
                }
                fullWidth
              />
            </Stack>
            <Stack direction="column" className="form-two-col-field">
              <Stack direction="row">
                <FormLabel>Auto Svg Chg</FormLabel>
                <Tooltip
                  title={tooltipComponent(
                    spaTooltipHeaders.serviceAutoServiceCharge,
                  )}
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </Stack>
              <Autocomplete
                size="small"
                options={SPA_SERVICES_YES_NO_OPTIONS}
                value={autoServiceCharge}
                onChange={(_, v) =>
                  setAutoServiceCharge(v ?? SPA_SERVICES_DEFAULT_NO)
                }
                renderInput={(params) => (
                  <TextField {...params} variant="outlined" />
                )}
              />
            </Stack>
            <Stack direction="column" className="form-two-col-field">
              <Stack direction="row">
                <FormLabel>Svg Chg %</FormLabel>
                <Tooltip
                  title={tooltipComponent(
                    spaTooltipHeaders.serviceAutoServiceChargePercentage,
                  )}
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </Stack>
              <TextField
                type="number"
                inputProps={{ step: 0.01 }}
                variant="outlined"
                size="small"
                value={autoServiceChargePct}
                onChange={(e) =>
                  setAutoServiceChargePct(
                    e.target.value === ""
                      ? ""
                      : parseFloat(e.target.value) || "",
                  )
                }
                fullWidth
              />
            </Stack>
            <Stack direction="column" className="form-two-col-field">
              <Stack direction="row">
                <FormLabel>Commissionable</FormLabel>
                <Tooltip
                  title={tooltipComponent(
                    spaTooltipHeaders.serviceCommissionable,
                  )}
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </Stack>
              <Autocomplete
                size="small"
                options={SPA_SERVICES_YES_NO_OPTIONS}
                value={commissionable}
                onChange={(_, v) =>
                  setCommissionable(v ?? SPA_SERVICES_DEFAULT_NO)
                }
                renderInput={(params) => (
                  <TextField {...params} variant="outlined" />
                )}
              />
            </Stack>
            <Stack direction="column" className="form-two-col-field">
              <Stack direction="row">
                <FormLabel>Available on Web</FormLabel>
                <Tooltip
                  title={tooltipComponent(
                    spaTooltipHeaders.serviceAvailableOnWeb,
                  )}
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </Stack>
              <Autocomplete
                size="small"
                options={SPA_SERVICES_YES_NO_OPTIONS}
                value={availableOnWeb}
                onChange={(_, v) =>
                  setAvailableOnWeb(v ?? SPA_SERVICES_DEFAULT_NO)
                }
                renderInput={(params) => (
                  <TextField {...params} variant="outlined" />
                )}
              />
            </Stack>
            <Stack
              direction="column"
              className="form-full-width-field"
              gap="12px"
            >
              <Stack direction="row" alignItems="center">
                <FormLabel component="legend">Available Days</FormLabel>
                <Tooltip
                  title={tooltipComponent(
                    spaTooltipHeaders.serviceAvailableDaysInAWeek,
                  )}
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </Stack>
              <Stack
                className="form-two-col-row"
                direction="row"
                flexWrap="wrap"
                gap="12px"
              >
                {availableDaysConfig.map(
                  ({ label, tooltip, value, setValue }) => (
                    <Stack
                      direction="column"
                      className="form-two-col-field"
                      key={label}
                    >
                      <Stack direction="row">
                        <FormLabel>{label}</FormLabel>
                        <Tooltip title={tooltipComponent(tooltip)}>
                          <InfoIcon fontSize="small" />
                        </Tooltip>
                      </Stack>
                      <Autocomplete
                        size="small"
                        options={SPA_SERVICES_YES_NO_OPTIONS}
                        value={value}
                        onChange={(_, v) =>
                          setValue(v ?? SPA_SERVICES_DEFAULT_YES)
                        }
                        renderInput={(params) => (
                          <TextField {...params} variant="outlined" />
                        )}
                      />
                    </Stack>
                  ),
                )}
              </Stack>
            </Stack>
            <Stack direction="column" className="form-two-col-field">
              <Stack direction="row">
                <FormLabel>Inactive</FormLabel>
                <Tooltip
                  title={tooltipComponent(spaTooltipHeaders.serviceInactive)}
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </Stack>
              <Autocomplete
                size="small"
                options={SPA_SERVICES_YES_NO_OPTIONS}
                value={inactive}
                onChange={(_, v) => setInactive(v ?? SPA_SERVICES_DEFAULT_NO)}
                renderInput={(params) => (
                  <TextField {...params} variant="outlined" />
                )}
              />
            </Stack>
            <Stack direction="column" className="form-two-col-field">
              <Stack direction="row">
                <FormLabel>Linked Retail Item *</FormLabel>
                <Tooltip
                  title={tooltipComponent(spaTooltipHeaders.serviceLinkedItem)}
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </Stack>
              <Autocomplete
                size="small"
                options={linkedRetailItemOptions}
                value={linkedRetailItemToOptionValue(
                  linkedItem,
                  linkedRetailItemOptions,
                )}
                disabled={!linkedRetailItemOptions.length}
                onChange={(_, v) => {
                  setLinkedItem(parseLinkedRetailItemFromOption(v));
                  setLinkedItemFieldError(false);
                }}
                isOptionEqualToValue={(a, b) => a === b}
                noOptionsText="No retail items available"
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    placeholder={
                      linkedRetailItemOptions.length
                        ? SPA_SERVICES_LINKED_ITEM_PLACEHOLDER
                        : "10 - Retail Item"
                    }
                    error={linkedItemFieldError}
                    helperText={
                      linkedItemFieldError
                        ? validationMessages.fieldRequired
                        : ""
                    }
                  />
                )}
              />
            </Stack>
            <Stack direction="column" className="form-full-width-field">
              <Stack direction="row">
                <FormLabel>Service Comment</FormLabel>
                <Tooltip
                  title={tooltipComponent(
                    spaTooltipHeaders.serviceServiceComment,
                  )}
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </Stack>
              <TextField
                variant="outlined"
                size="small"
                multiline
                minRows={2}
                value={serviceComment ?? ""}
                onChange={(e) => setServiceComment(e.target.value || null)}
                fullWidth
              />
            </Stack>
          </Stack>
        </LocalizationProvider>
        <Stack direction="row" justifyContent="flex-end" mt={2}>
          <Button
            variant="contained"
            sx={{
              cursor: "pointer",
              border: "1px solid gray",
              borderRadius: "10px",
              background: "darkgreen",
              color: "white",
            }}
            onClick={() => (selectedId ? updateItem() : addItem())}
          >
            {selectedId ? buttonValues.UPDATE : buttonValues.ADD} Service
          </Button>
        </Stack>
        <Stack direction="column" gap="10px" mt={4}>
          <Typography variant="body2" sx={{ color: "red", fontWeight: 800 }}>
            Do not use sample data. Those are for demo purposes only!
          </Typography>
          <Typography variant="h6">Services Available</Typography>
          {items ? (
            <DraggableItemList
              itemData={items}
              onOrderUpdate={(order) => setItems(order)}
              onEdit={editItem}
              onDelete={deleteItemById}
              code={DNDFieldsEnum.SERVICECODE}
              name={DNDFieldsEnum.SERVICEDESCRIPTION}
            />
          ) : (
            <Typography variant="body2">No Services Available</Typography>
          )}
        </Stack>
      </Paper>
      <AlertComponent
        message={alertMessage}
        type={alertType}
        alertOpen={openAlert}
        closeAlert={() => setOpenAlert(false)}
      />
    </Stack>
  );
};

export default SpaServices;
